import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { queryKeys } from '@shared/lib/react-query';
import { instantiesService, type InstantieNiveauHierarchy } from '@shared/services/instanties.service';
import { zaakTypesService } from '@shared/services/zaakTypes.service';
import { themasService, type ThemaHierarchyWithCounts } from '@shared/services/themas.service';
import { wetsartikelenService } from '@shared/services/wetsartikelen.service';

export interface FilterCountsParams {
  searchQuery?: string;
  selectedDomains?: string[];
  selectedCourts?: string[];
  selectedThemas?: string[];
  selectedWetsartikelen?: string[];
  caseType?: string;
  dateFrom?: string;
  dateTo?: string;
  portalIds: string[];
}

export interface WetsartikelHierarchyItem {
  wetboek: string;
  wetboek_afkorting: string;
  totalCount: number;
  artikelen: {
    artikel_nummer: string;
    totalCount: number;
    items: {
      id: string;
      label: string;
      count: number;
    }[];
  }[];
}

export interface FilterOption {
  id: string;
  label: string;
  count: number;
}

export interface FilterCountsData {
  instantieHierarchy: InstantieNiveauHierarchy[];
  caseTypes: FilterOption[];
  themaHierarchy: ThemaHierarchyWithCounts[];
  wetsartikelHierarchy: WetsartikelHierarchyItem[];
}

/**
 * Hook for fetching filter counts with React Query caching.
 *
 * This replaces the manual useState + useEffect pattern in SearchFilters,
 * providing automatic caching, deduplication, and stale-while-revalidate.
 *
 * Benefits:
 * - Automatic request deduplication when multiple components need the same data
 * - Stale-while-revalidate: shows cached data immediately while fetching fresh data
 * - Built-in error handling and retry logic
 * - Prevents unnecessary re-fetches when filters haven't changed
 */
export function useFilterCounts(params: FilterCountsParams) {
  const {
    searchQuery,
    selectedDomains,
    selectedCourts,
    selectedThemas,
    selectedWetsartikelen,
    caseType,
    dateFrom,
    dateTo,
    portalIds,
  } = params;

  return useQuery({
    queryKey: queryKeys.filters.counts({
      searchQuery,
      selectedDomains,
      selectedCourts,
      selectedThemas,
      selectedWetsartikelen,
      caseType,
      dateFrom,
      dateTo,
      portalIds,
    }),
    queryFn: async (): Promise<FilterCountsData> => {
      // Fetch all filter options in parallel
      const [instantieHierarchie, zaakTypes, themasData, wetsartikelenData] = await Promise.all([
        instantiesService.getHierarchyWithCounts({
          searchQuery: searchQuery || undefined,
          rechtsgebiedIds: selectedDomains?.length ? selectedDomains : undefined,
          zaakTypeIds: caseType && caseType !== 'all' ? [caseType] : undefined,
          dateFrom,
          dateTo,
          portalIds,
        }),
        zaakTypesService.getWithCaseCount({
          searchQuery: searchQuery || undefined,
          rechtsgebiedIds: selectedDomains?.length ? selectedDomains : undefined,
          instantieIds: selectedCourts?.length ? selectedCourts : undefined,
          dateFrom,
          dateTo,
          portalIds,
        }),
        themasService.getHierarchyWithCounts({
          searchQuery: searchQuery || undefined,
          rechtsgebiedIds: selectedDomains?.length ? selectedDomains : undefined,
          instantieIds: selectedCourts?.length ? selectedCourts : undefined,
          zaakTypeIds: caseType && caseType !== 'all' ? [caseType] : undefined,
          wetsartikelIds: selectedWetsartikelen?.length ? selectedWetsartikelen : undefined,
          dateFrom,
          dateTo,
          portalIds,
        }),
        wetsartikelenService.getHierarchyWithCounts({
          searchQuery: searchQuery || undefined,
          rechtsgebiedIds: selectedDomains?.length ? selectedDomains : undefined,
          instantieIds: selectedCourts?.length ? selectedCourts : undefined,
          zaakTypeIds: caseType && caseType !== 'all' ? [caseType] : undefined,
          dateFrom,
          dateTo,
          portalIds,
        }),
      ]);

      // Transform zaak types to filter options
      const caseTypesFormatted: FilterOption[] = zaakTypes
        .map(zt => ({
          id: zt.id,
          label: zt.naam,
          count: zt.case_count,
        }))
        .sort((a, b) => b.count - a.count);

      // Transform wetsartikelen to hierarchical structure
      const hierarchyMap = new Map<string, WetsartikelHierarchyItem>();

      wetsartikelenData.forEach(wa => {
        // Skip items with 0 count
        if (wa.item_case_count === 0) return;

        const key = wa.wetboek_afkorting;

        if (!hierarchyMap.has(key)) {
          hierarchyMap.set(key, {
            wetboek: wa.wetboek,
            wetboek_afkorting: wa.wetboek_afkorting,
            totalCount: wa.wetboek_case_count,
            artikelen: [],
          });
        }

        const wetboekGroup = hierarchyMap.get(key)!;

        // Find or create artikel group
        let artikelGroup = wetboekGroup.artikelen.find(a => a.artikel_nummer === wa.artikel_nummer);
        if (!artikelGroup) {
          artikelGroup = {
            artikel_nummer: wa.artikel_nummer,
            totalCount: wa.artikel_case_count,
            items: [],
          };
          wetboekGroup.artikelen.push(artikelGroup);
        }

        artikelGroup.items.push({
          id: wa.wetsartikel_id,
          label: wa.volledige_referentie,
          count: wa.item_case_count,
        });
      });

      // Convert to array and sort
      const wetsartikelHierarchy = Array.from(hierarchyMap.values())
        .map(wetboek => ({
          ...wetboek,
          artikelen: wetboek.artikelen
            .map(artikel => ({
              ...artikel,
              items: artikel.items.sort((a, b) => b.count - a.count),
            }))
            .sort((a, b) => b.totalCount - a.totalCount),
        }))
        .sort((a, b) => b.totalCount - a.totalCount);

      return {
        instantieHierarchy: instantieHierarchie,
        caseTypes: caseTypesFormatted,
        themaHierarchy: themasData,
        wetsartikelHierarchy,
      };
    },
    // Keep previous data while fetching new data (prevents UI flicker)
    placeholderData: keepPreviousData,
    // Cache filter counts for 30 seconds before considering stale
    // This prevents excessive re-fetching while still keeping data relatively fresh
    staleTime: 30 * 1000,
    // Keep data in cache for 5 minutes
    gcTime: 5 * 60 * 1000,
  });
}
